// src/components/TemplatePlantillaPro.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import TemplatePlantillaProStyles from "./TemplatePlantillaPro/TemplatePlantillaProStyles";
import WhatsAppPanel from "./TemplatePlantillaPro/WhatsAppPanel";
import EmailPanel from "./TemplatePlantillaPro/EmailPanel";

function Tabs({ tab, setTab, visibleTabs }) {
  return (
    <div className="tabs">
      {visibleTabs.includes("wa") && (
        <button
          className={`tab ${tab === "wa" ? "active" : ""}`}
          onClick={() => setTab("wa")}
        >
          WhatsApp
        </button>
      )}
      {visibleTabs.includes("email") && (
        <button
          className={`tab ${tab === "email" ? "active" : ""}`}
          onClick={() => setTab("email")}
        >
          Email
        </button>
      )}
      <button
        className={`tab ${tab === "help" ? "active" : ""}`}
        onClick={() => setTab("help")}
      >
        Ayuda
      </button>
    </div>
  );
}

function HelpPanel() {
  return (
    <div className="field">
      <div className="label">Guía rápida</div>
      <div style={{ fontSize: 13, lineHeight: 1.6, color: "#cdd7ea" }}>
        Variables <code>{`{nombre}`}</code>, <code>{`{fecha}`}</code>… • WhatsApp
        soporta formato • Email admite HTML o texto. <br />
        Ahora puedes definir <strong>varios mensajes de WhatsApp</strong> dentro de
        esta misma plantilla (por ejemplo: mensaje inicial, recordatorio, follow-up).
      </div>
    </div>
  );
}

/* Barra simple para insertar variables en asunto / body / designer */
function VariablesToolbar({
  columns = [],
  onInsert, // (placeholder, target) => void
}) {
  const safeCols = Array.isArray(columns) && columns.length ? columns : ["nombre", "fecha", "email"];
  return (
    <div style={{ marginBottom: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ color: "#9ca3af", fontSize: 12 }}>Variables:</div>
      {safeCols.map((col) => {
        const ph = `{${col}}`;
        return (
          <div key={col} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <button
              type="button"
              className="btn btn-outline"
              title={`Insertar ${ph} en asunto`}
              onClick={() => onInsert(ph, "subject")}
              style={{ fontSize: 12, padding: "4px 8px" }}
            >
              {ph}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              title={`Insertar ${ph} en cuerpo`}
              onClick={() => onInsert(ph, "body")}
              style={{ fontSize: 12, padding: "4px 8px" }}
            >
              body
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              title={`Insertar ${ph} en designer HTML`}
              onClick={() => onInsert(ph, "designer")}
              style={{ fontSize: 12, padding: "4px 8px" }}
            >
              html
            </button>
          </div>
        );
      })}
    </div>
  );
}

function PanelGrid({ left, right }) {
  return (
    <div className="panel">
      <div className="gridLL">
        <div style={{ display: "grid", gap: 12 }}>{left}</div>
        <div style={{ display: "grid", gap: 12 }}>{right}</div>
      </div>
    </div>
  );
}

function TemplatePlantillaPro({
  // Nombre de la plantilla
  name = "",
  setName = () => {},

  // Compatibilidad vieja (una sola plantilla WA)
  template = "",
  setTemplate = () => {},

  // Nuevo: lista de plantillas de WhatsApp
  templatesWa,
  setTemplatesWa,

  // Variables disponibles (columnas)
  columns = [],

  // Email
  sendEmail = false,
  setSendEmail = () => {},
  templateEmailSubject = "",
  setTemplateEmailSubject = () => {},
  templateEmailBody = "",
  setTemplateEmailBody = () => {},

  // Modal / acciones
  modal = false,
  onClose = () => {},
  title = "Editor de plantilla",
  primaryLabel = "Guardar",
  onPrimary = () => {},
  secondaryLabel = "Cerrar",
  onSecondary = onClose,

  // flags
  isWhatsApp = undefined,
  isEmail = undefined,
  isWhatsAppBusiness = false,
  cloudAccounts = [],
  selectedCloudAccountId = "",
  setSelectedCloudAccountId = () => {},
}) {
  const [tab, setTab] = useState("wa"); // "wa" | "email" | "help"
  const [zoom, setZoom] = useState(1);
  const [emailPreview, setEmailPreview] = useState("");

  // Diseñador de email
  const [designerMode, setDesignerMode] = useState(true);
  const [designer, setDesigner] = useState({
    logo: "",
    title: "Tu Empresa",
    accent: "#2563eb",
    footerText: "© {year} Tu Empresa. Todos los derechos reservados.",
    width: 640,
  });
  const [designerBodyHtml, setDesignerBodyHtml] = useState("");

  /* -------------------- WhatsApp: múltiples plantillas -------------------- */

  const [waList, setWaList] = useState(() => {
    if (Array.isArray(templatesWa) && templatesWa.length) {
      return templatesWa.map((tpl, idx) => ({
        id: tpl.id ?? idx + 1,
        name: tpl.name || `Mensaje ${idx + 1}`,
        text: tpl.text ?? tpl.template ?? "",
        delay: tpl.delay ?? 0,
      }));
    }
    return [
      {
        id: 1,
        name: "Mensaje principal",
        text: template || "",
        delay: 0,
      },
    ];
  });

  useEffect(() => {
    if (typeof setTemplatesWa === "function") {
      setTemplatesWa(waList);
    }
    if (typeof setTemplate === "function") {
      setTemplate(waList[0]?.text || "");
    }
  }, [waList, setTemplatesWa, setTemplate]);

  const addWaTemplate = () => {
    setWaList((list) => [
      ...list,
      {
        id: Date.now(),
        name: `Mensaje ${list.length + 1}`,
        text: "",
        delay: 0,
      },
    ]);
  };

  const removeWaTemplate = (id) => {
    setWaList((list) => {
      const filtered = list.filter((t) => t.id !== id);
      if (!filtered.length) {
        return [
          {
            id: 1,
            name: "Mensaje principal",
            text: "",
            delay: 0,
          },
        ];
      }
      return filtered;
    });
  };

  const updateWaTemplate = (id, patch) => {
    setWaList((list) =>
      list.map((tpl) => (tpl.id === id ? { ...tpl, ...patch } : tpl))
    );
  };

  /* ------------------ decidir pestañas visibles ------------------ */
  const visibleTabs = useMemo(() => {
    if (isWhatsApp === true && isEmail === true) return ["wa", "email"];
    if (isWhatsApp === true && isEmail === false) return ["wa"];
    if (isWhatsApp === false && isEmail === true) return ["email"];
    return ["wa", "email"];
  }, [isWhatsApp, isEmail]);

  useEffect(() => {
    if (!visibleTabs.includes(tab)) {
      setTab(visibleTabs[0] || "help");
    }
  }, [visibleTabs, tab]);

  /* ------------------------- Contenido principal -------------------------- */

  // refs para controlar foco/inserción
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  const handleInsertVariable = (placeholder, target) => {
    // target: "subject" | "body" | "designer"
    if (target === "subject") {
      // insertar en asunto usando setter
      // intentamos respetar cursor si existe un input asociado y es accesible vía subjectRef
      const cur = subjectRef.current;
      if (cur && typeof cur.setSelectionRange === "function") {
        const start = cur.selectionStart ?? cur.value.length;
        const end = cur.selectionEnd ?? start;
        const newVal =
          cur.value.slice(0, start) + placeholder + cur.value.slice(end);
        setTemplateEmailSubject(newVal);
        // actualizamos el textarea/input value programáticamente
        // mantener el cursor después del placeholder
        setTimeout(() => {
          try {
            cur.focus();
            cur.setSelectionRange(start + placeholder.length, start + placeholder.length);
          } catch (e) {}
        }, 0);
        return;
      }
      // fallback: append
      setTemplateEmailSubject((s) => (s || "") + placeholder);
      return;
    }

    if (target === "body") {
      const cur = bodyRef.current;
      if (cur && typeof cur.setSelectionRange === "function") {
        const start = cur.selectionStart ?? cur.value.length;
        const end = cur.selectionEnd ?? start;
        const newVal =
          cur.value.slice(0, start) + placeholder + cur.value.slice(end);
        setTemplateEmailBody(newVal);
        setTimeout(() => {
          try {
            cur.focus();
            cur.setSelectionRange(start + placeholder.length, start + placeholder.length);
          } catch (e) {}
        }, 0);
        return;
      }
      setTemplateEmailBody((b) => (b || "") + placeholder);
      return;
    }

    if (target === "designer") {
      // Insertar en HTML del diseñador (si está en modo designer)
      if (designerMode) {
        setDesignerBodyHtml((h) => (h || "") + placeholder);
      } else {
        // si no está en designerMode, caer a cuerpo
        setTemplateEmailBody((b) => (b || "") + placeholder);
      }
    }
  };

  const Main = useMemo(() => {
    return (
      <>
        <Tabs tab={tab} setTab={setTab} visibleTabs={visibleTabs} />

        {/* 🟢 pestaña WhatsApp */}
        {tab === "wa" && visibleTabs.includes("wa") && (
          <PanelGrid
            left={
              <>
                {isWhatsAppBusiness && (
                  <div style={{ padding: 8, background: "#1f2937", borderRadius: 8 }}>
                    <strong>WhatsApp Business API:</strong>{" "}
                    Esta plantilla requiere aprobación de Meta y debe asociarse a una cuenta Cloud/WABA.
                    <div style={{ marginTop: 8 }}>
                      <label style={{ display: "grid", gap: 4 }}>
                        <span style={{ fontSize: 12, color: "#cbd5e1" }}>Cuenta Cloud / WABA destino</span>
                        <select
                          className="input"
                          value={selectedCloudAccountId || ""}
                          onChange={(e) => setSelectedCloudAccountId(e.target.value)}
                        >
                          <option value="">Selecciona cuenta Cloud</option>
                          {(cloudAccounts || []).map((acc) => (
                            <option key={acc.id} value={acc.id}>
                              {acc.label} - {acc.phone}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
                        La plantilla se aprueba por WABA. Si varios numeros pertenecen al mismo WABA,
                        podran usar la misma plantilla aprobada.
                      </div>
                    </div>
                  </div>
                )}

                {waList.map((tpl, index) => (
                  <div
                    key={tpl.id}
                    style={{
                      border: "1px solid #26334f",
                      borderRadius: 12,
                      padding: 12,
                      display: "grid",
                      gap: 8,
                      background: "#020817",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span
                          style={{
                            fontSize: 11,
                            color: "#6b7280",
                            textTransform: "uppercase",
                          }}
                        >
                          Plantilla WhatsApp #{index + 1}
                        </span>
                        <input
                          className="input"
                          style={{
                            height: 30,
                            padding: "4px 8px",
                            fontSize: 12,
                          }}
                          value={tpl.name}
                          onChange={(e) =>
                            updateWaTemplate(tpl.id, {
                              name: e.target.value,
                            })
                          }
                          placeholder="Nombre interno del mensaje (ej: Recordatorio 24h)"
                        />
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 4,
                        }}
                      >
                        <div style={{ display: "flex", gap: 4, fontSize: 10 }}>
                          <span
                            style={{ color: "#9ca3af", whiteSpace: "nowrap" }}
                          >
                            Delay (min)
                          </span>
                          <input
                            type="number"
                            min={0}
                            className="input"
                            style={{
                              width: 70,
                              height: 26,
                              padding: "2px 6px",
                              fontSize: 11,
                            }}
                            value={tpl.delay ?? 0}
                            onChange={(e) =>
                              updateWaTemplate(tpl.id, {
                                delay: Number(e.target.value) || 0,
                              })
                            }
                          />
                        </div>

                        {waList.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-muted"
                            style={{
                              padding: "4px 8px",
                              fontSize: 10,
                            }}
                            onClick={() => removeWaTemplate(tpl.id)}
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </div>

                    <WhatsAppPanel
                      template={tpl.text}
                      setTemplate={(val) =>
                        updateWaTemplate(tpl.id, { text: val })
                      }
                      columns={columns}
                      zoom={zoom}
                      setZoom={setZoom}
                    />
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ marginTop: 4 }}
                  onClick={addWaTemplate}
                >
                  + Agregar mensaje de WhatsApp
                </button>

                <div className="hint">
                  Se guardará la lista completa de mensajes. Tu backend puede
                  enviar uno o varios según la lógica de la campaña / flujo.
                </div>
              </>
            }
            right={null}
          />
        )}

        {/* ✉️ pestaña Email (barra de variables incluida) */}
        {tab === "email" && visibleTabs.includes("email") && (
          <PanelGrid
            left={
              <>
                {/* Variables toolbar */}
                <VariablesToolbar
                  columns={columns}
                  onInsert={(ph, target) => handleInsertVariable(ph, target)}
                />

                <EmailPanel
                  sendEmail={sendEmail}
                  setSendEmail={setSendEmail}
                  templateEmailSubject={templateEmailSubject}
                  setTemplateEmailSubject={setTemplateEmailSubject}
                  templateEmailBody={templateEmailBody}
                  setTemplateEmailBody={setTemplateEmailBody}
                  designerMode={designerMode}
                  setDesignerMode={setDesignerMode}
                  designer={designer}
                  setDesigner={setDesigner}
                  designerBodyHtml={designerBodyHtml}
                  setDesignerBodyHtml={setDesignerBodyHtml}
                  emailPreview={emailPreview}
                  setEmailPreview={setEmailPreview}
                  zoom={zoom}
                  // refs para posibilidad de inserción directa (EmailPanel puede usar estos refs si los expone)
                  subjectRef={subjectRef}
                  bodyRef={bodyRef}
                  // columns para que EmailPanel pueda renderizar ayudas si lo desea
                  columns={columns}
                />
              </>
            }
            right={null}
          />
        )}

        {/* ❓ pestaña Ayuda */}
        {tab === "help" && (
          <div className="panel">
            <HelpPanel />
          </div>
        )}
      </>
    );
  }, [
    tab,
    zoom,
    waList,
    columns,
    sendEmail,
    setSendEmail,
    templateEmailSubject,
    setTemplateEmailSubject,
    templateEmailBody,
    setTemplateEmailBody,
    designerMode,
    designer,
    designerBodyHtml,
    emailPreview,
    visibleTabs,
    isWhatsAppBusiness,
  ]);

  /* ------------------------- Render: embebido o modal ------------------------- */

  if (!modal) {
    return (
      <section className="card section !p-4 md:!p-6">
        <TemplatePlantillaProStyles />
        {Main}
      </section>
    );
  }

  return (
    <div
      className="tplp-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && e.preventDefault()}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="tplp-modal" onClick={(e) => e.stopPropagation()}>
        <TemplatePlantillaProStyles />
        <div className="tplp-head">
          <div className="tplp-title">{title}</div>
          <div
            className="tplp-name"
            style={{ marginLeft: 12, flex: 1, maxWidth: 420 }}
          >
            <input
              className="input"
              placeholder="Nombre de la plantilla"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="tplp-actions">
            <button className="btn btn-muted" onClick={onSecondary}>
              {secondaryLabel}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                // Construimos payload con status si corresponde
                const status = isWhatsAppBusiness ? "pending_approval" : null;
                const payload = {
                  name,
                  whatsappTemplates: waList,
                  email: {
                    enabled: !!sendEmail,
                    subject: templateEmailSubject,
                    body: templateEmailBody,
                  },
                  is_whatsapp: visibleTabs.includes("wa") ? 1 : 0,
                  is_email: visibleTabs.includes("email") ? 1 : 0,
                  is_whatsapp_business: isWhatsAppBusiness ? 1 : 0,
                  meta_wa_account_id: isWhatsAppBusiness ? selectedCloudAccountId || null : null,
                  status,
                  approved_by: null,
                };
                onPrimary(payload);
              }}
            >
              {primaryLabel}
            </button>
          </div>
        </div>
        {Main}
      </div>
    </div>
  );
}

export default React.memo(TemplatePlantillaPro);
